import { useRequest } from "../../../hooks/useRequest";
import useHttpResponseAlert from "../../../hooks/useHttpResponseAlert";
import { FormikValues } from "formik";
import {
  AssetType,
  Intervention,
  InterventionType,
  AssessmentMethodology,
  Outcome,
  RiskMonitoringForm,
} from "../types";
import Utils from "../utils";
import routes from "../../../routes";

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface DateRange {
  start: number | null;
  end: number | null;
}

export interface ProgrammeInfo {
  carbonCreditProgramme: Array<string> | string;
  projectDescriptionDocument: string;
  accountingMethodology: string;
  assetType: AssetType | null;
}

export interface InternalUseOnly {
  websiteUrl: string;
  xeroAccountNumber: string;
  researchTeamNotes: string;
  additionalResourceLink: string;
}

export interface RiskMonitoring {
  isEnabled: boolean;
  buffer?: number;
  geospatialFile?: File | null | undefined;
  geospatialDataId?: string;
}

type Outcomes = Array<{
  value: number | string;
  unit: string;
  isUnitOfSale: boolean;
  startDate?: number | null;
  endDate?: number | null;
}>;

export interface SolutionPayload {
  name: string;
  vbcWebsiteDisplayName?: string | null;
  developer: string | null;
  shortDescription: string | null;
  country: Array<string>;
  coordinates: Coordinates | null;
  developmentStage: Array<string>;
  assessmentMethodology: AssessmentMethodology | null;
  interventionType?: InterventionType | null;
  SDG: Array<number>;
  date: DateRange;
  isReadyForSale: boolean;
  isUsedForCarbonOffsetting: boolean;
  interventions: Array<Intervention>;
  outcomes: Outcomes;
  programmeInfo: ProgrammeInfo;
  internalUseOnly: InternalUseOnly;
  riskMonitoring?: RiskMonitoring;
}

export interface SolutionFormValues extends FormikValues {
  "solution-name": string;
  "solution-vbc-website-display-name"?: string;
  "solution-developer": string;
  "solution-short-description": string;
  "country-select": Array<string> | null;
  latitude: string;
  longitude: string;
  "solution-development-stage": Array<string>;
  "solution-assessment-methodology": AssessmentMethodology | null;
  "solution-intervention-type": InterventionType | null;
  "solution-sdg": Array<number>;
  "solution-start-date": string | null;
  "solution-end-date": string | null;
  "solution-is-ready-for-sale": boolean;
  "solution-is-carbon-offset": boolean;
  interventions: Array<Intervention>;
  outcomes: Array<Outcome>;
  "programme-info": {
    "carbon-crediting-programme": Array<string> | string;
    "description-document": string;
    "carbon-accounting-methodology": string;
    "asset-type": AssetType | null;
  };
  "internal-details": {
    "website-url": string;
    "xero-account-number": string;
    "research-team-notes": string;
    "additional-resources-link": string;
  };
  "risk-monitoring": RiskMonitoringForm;
}

function useAddSolutionForm() {
  const { fetch, response, error, loading } = useRequest();

  const parseTimestamp = (timestamp: number | string | null | undefined) =>
    timestamp !== null && timestamp !== undefined
      ? (() => {
          const parsedTimestamp = parseInt(timestamp.toString());
          return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
        })()
      : null;

  const { alert, setAlert } = useHttpResponseAlert(
    error,
    response,
    "New project has been added successfully!",
  );

  const handleSubmit = (form: SolutionFormValues) => {
    const payload: SolutionPayload = {
      name: form["solution-name"],
      vbcWebsiteDisplayName: form["solution-vbc-website-display-name"],
      developer: form["solution-developer"],
      shortDescription: form["solution-short-description"],
      country: form["country-select"] || [],
      coordinates: {
        latitude: parseFloat(form["latitude"]),
        longitude: parseFloat(form["longitude"]),
      },
      developmentStage: form["solution-development-stage"],
      assessmentMethodology: form["solution-assessment-methodology"],
      interventionType: form["solution-intervention-type"],
      SDG: form["solution-sdg"],
      date: {
        start: parseInt(form["solution-start-date"] || "0"),
        end: parseInt(form["solution-end-date"] || "0"),
      },
      isReadyForSale: form["solution-is-ready-for-sale"],
      isUsedForCarbonOffsetting: form["solution-is-carbon-offset"],
      interventions: form.interventions.map((intervention) => ({
        action: intervention.action ?? [],
        ecosystem: intervention.ecosystem ?? [],
      })),
      outcomes: form["outcomes"].map((outcome: Outcome) => ({
        value: parseFloat(outcome.value),
        unit: outcome.unit,
        isUnitOfSale: outcome["unit-of-sale"],
        startDate: parseTimestamp(outcome["outcome-start-date"]),
        endDate: parseTimestamp(outcome["outcome-end-date"]),
      })),
      programmeInfo: {
        carbonCreditProgramme:
          form["programme-info"]["carbon-crediting-programme"],
        projectDescriptionDocument:
          form["programme-info"]["description-document"],
        accountingMethodology:
          form["programme-info"]["carbon-accounting-methodology"],
        assetType: form["programme-info"]["asset-type"],
      },
      internalUseOnly: {
        websiteUrl: form["internal-details"]["website-url"],
        xeroAccountNumber: form["internal-details"]["xero-account-number"],
        researchTeamNotes: form["internal-details"]["research-team-notes"],
        additionalResourceLink:
          form["internal-details"]["additional-resources-link"],
      },
      riskMonitoring: {
        isEnabled: form["risk-monitoring"]["is-risk-alert-enabled"] || false,
        buffer: form["risk-monitoring"]["buffer-value-km"] || 10,
        geospatialFile: form["risk-monitoring"]["geospatial-file"] || null,
      },
    };

    const basePayload = Utils.SolutionFormBuilder.removeNonValues(
      payload,
    ) as Omit<SolutionPayload, "outcomes"> & {
      outcomes?: Outcomes;
    };
    const normalizedOutcomes = basePayload.outcomes || [];
    const normalisedPayload = {
      ...basePayload,
      outcomes: {
        v2: normalizedOutcomes,
        v3: normalizedOutcomes,
      },
    };

    const geojsonFile = normalisedPayload?.riskMonitoring?.geospatialFile;

    if (!(geojsonFile instanceof File)) {
      return fetch("POST", routes.api.solution.base.pattern, normalisedPayload);
    }

    const multipartForm = new FormData();
    const solutionMetadata = {
      ...normalisedPayload,
      riskMonitoring: {
        ...normalisedPayload.riskMonitoring,
        geospatialFile: null,
      },
    };

    multipartForm.set("payload", JSON.stringify(solutionMetadata));
    multipartForm.set("file", geojsonFile, geojsonFile.name);
    return fetch(
      "POST",
      routes.api.solution.base.pattern,
      multipartForm,
      undefined,
      false,
    );
  };

  return {
    handleSubmit,
    formSubmittalLoading: loading,
    alert,
    setAlert,
    formError: error,
    formSuccess: response,
  };
}

export default useAddSolutionForm;
